import { CADENCE_PRESETS } from '@forwardflow/features/campaign-flow/data/mock'
import {
   IConversationDefinition,
   IPhoneDefinition,
   IStrategyDefinition,
} from '@forwardflow/features/campaign-v2/interfaces/campaign-v2.types'
import { IPlaybook } from '@forwardflow/features/campaign-flow/interfaces/campaign-flow.types'
import { syncMessages } from '@forwardflow/features/campaign-flow/utils/playbook'
import { fromTemplate } from '@forwardflow/features/campaign-flow/utils/text'

/**
 * Library content → the playbook the screens edit (campaign v2 handoff,
 * section 5). The backend has no recommended cadence, so timing starts from
 * the balanced preset; texts and voicemails are then fitted to it.
 */
export interface IContentSnapshot {
   phone: { name: string; definition: IPhoneDefinition } | null
   strategy: { name: string; definition: IStrategyDefinition } | null
   conversation: { name: string; definition: IConversationDefinition } | null
}

export const playbookFromContent = (
   c: IContentSnapshot,
   fallback: IPlaybook
): IPlaybook => {
   const sections = (c.phone?.definition.sections || [])
      .filter((s) => s.type === 'call_outbound')
      .sort((a, b) => a.order - b.order)
   const script = sections.length
      ? sections.map((s, i) => ({
           id: String(s.sectionId ?? `s${i + 1}`),
           title: s.name,
           text: fromTemplate(s.content),
        }))
      : fallback.script
   const sms = c.strategy?.definition.sms || []
   const vms = c.strategy?.definition.voicemail || []
   return syncMessages({
      ...fallback,
      origin: 'recommended',
      scriptName: c.phone?.name || fallback.scriptName,
      messageGroupName: c.strategy?.name || fallback.messageGroupName,
      preset: 'balanced',
      touches: CADENCE_PRESETS.balanced.touches.map((t) => ({ ...t })),
      script,
      texts: sms.length
         ? sms.map((m, i) => ({
              id: `s${i + 1}`,
              text: fromTemplate(m.template),
           }))
         : fallback.texts,
      voicemails: vms.length
         ? vms.map((m, i) => ({
              id: `v${i + 1}`,
              text: fromTemplate(m.template),
           }))
         : fallback.voicemails,
      replyInstructions:
         c.conversation?.definition.content || fallback.replyInstructions,
   })
}

/** What the content calls care about. Timing lives in the configuration, not in content. */
export const contentHash = (pb: IPlaybook) =>
   JSON.stringify({
      s: pb.script.map((x) => [x.title, x.text]),
      t: pb.texts.map((x) => x.text),
      v: pb.voicemails.map((x) => x.text),
      r: pb.replyInstructions,
   })
